import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsx mdx */

import DefaultLayout from "/opt/buildhome/repo/src/layouts/docs.js";
export const _frontmatter = {};

const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};

const Link = makeShortcode("Link");
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <p>{`The pages that follow contain technical reference information on the Uniswap SDK.
Looking for a `}<Link to='/docs/v2/javascript-SDK/quick-start' mdxType="Link">{`quickstart`}</Link>{` instead?
You may also want to jump into a `}<Link to='/docs/v2/javascript-SDK/fetching-data' mdxType="Link">{`guide`}</Link>{`,
which offers a friendlier introduction to the SDK!`}</p>
    <p>{`The SDK is written in TypeScript, has a robust test suite, performs arbitrary precision arithmetic,
and supports rounding to significant digits or fixed decimal places.
The principal exports of the SDK are `}<em parentName="p">{`entities`}</em>{`: classes that contain initialization and validation checks,
necessary data fields, and helper functions.`}</p>
    <p>{`An important concept in the SDK is `}<em parentName="p">{`fractions`}</em>{`. Because Solidity performs integer math, care must be taken in
non-EVM environments to faithfully replicate the actual computation carried out on-chain.
The first concern here is to ensure that an overflow-safe integer implementation is used.
Ideally, the SDK would be able to use native `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/BigInt"
      }}>{`BigInt`}</a>{`s.
However, until support becomes more widespread, `}<a parentName="p" {...{
        "href": "https://github.com/GoogleChromeLabs/jsbi"
      }}>{`JSBI`}</a>{` objects are used instead,
with the idea that once BigInts proliferate, this dependency can be compiled away.
The second concern is precision loss due to, for example, chained price ratio calculations.
To address this issue, all math operations are performed as fraction operations, ensuring arbitrary precision up
until the point that values are rounded for display purposes, or truncated to fit inside a fixed bit width.`}</p>
    <p>{`The SDK works for all chains on which the `}<Link to='/docs/v2/smart-contracts/factory#address' mdxType="Link">{`factory`}</Link>{` is deployed.`}</p>
    <h2 {...{
      "id": "code",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h2" {...{
        "href": "#code",
        "aria-label": "code permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "aria-hidden": "true",
          "focusable": "false",
          "height": "16",
          "version": "1.1",
          "viewBox": "0 0 16 16",
          "width": "16"
        }}><path parentName="svg" {...{
            "fillRule": "evenodd",
            "d": "M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z"
          }}></path></svg></a>{`Code`}</h2>
    <p>{`The `}<a parentName="p" {...{
        "href": "https://github.com/Uniswap/uniswap-sdk"
      }}>{`source code is available on GitHub`}</a>{`.`}</p>
    <h2 {...{
      "id": "dependencies",
      "style": {
        "position": "relative"
      }
    }}><a parentName="h2" {...{
        "href": "#dependencies",
        "aria-label": "dependencies permalink",
        "className": "anchor before"
      }}><svg parentName="a" {...{
          "aria-hidden": "true",
          "focusable": "false",
          "height": "16",
          "version": "1.1",
          "viewBox": "0 0 16 16",
          "width": "16"
        }}><path parentName="svg" {...{
            "fillRule": "evenodd",
            "d": "M4 9h1v1H4c-1.5 0-3-1.69-3-3.5S2.55 3 4 3h4c1.45 0 3 1.69 3 3.5 0 1.41-.91 2.72-2 3.25V8.59c.58-.45 1-1.27 1-2.09C10 5.22 8.98 4 8 4H4c-.98 0-2 1.22-2 2.5S3 9 4 9zm9-3h-1v1h1c1 0 2 1.22 2 2.5S13.98 12 13 12H9c-.98 0-2-1.22-2-2.5 0-.83.42-1.64 1-2.09V6.25c-1.09.53-2 1.84-2 3.25C6 11.31 7.55 13 9 13h4c1.45 0 3-1.69 3-3.5S14.5 6 13 6z"
          }}></path></svg></a>{`Dependencies`}</h2>
    <p>{`The SDK declares its dependencies as `}<a parentName="p" {...{
        "href": "https://github.com/Uniswap/uniswap-sdk/blob/v2/package.json#L33"
      }}>{`peer dependencies`}</a>{`.
This is for two reasons:`}</p>
    <ul>
      <li parentName="ul">{`prevent installation of unused dependencies (e.g. `}<inlineCode parentName="li">{`@ethersproject/providers`}</inlineCode>{` and `}<inlineCode parentName="li">{`@ethersproject/contracts`}</inlineCode>{`, only used in `}<a parentName="li" {...{
          "href": "/docs/v2/SDK/fetcher"
        }}><inlineCode parentName="a">{`Fetcher`}</inlineCode></a>{`)`}</li>
      <li parentName="ul">{`prevent duplicate `}<inlineCode parentName="li">{`@ethersproject`}</inlineCode>{` dependencies with conflicting versions`}</li>
    </ul>
    <p>{`However, this means you must install these dependencies alongside the SDK, if you do not already have them installed.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      